import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Zap, User, Phone, DollarSign, FileText, Send, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickQuote() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: form, 2: preview, 3: sent
  const [formData, setFormData] = useState({
    client_name: '',
    client_phone: '',
    description: '',
    quote_amount: ''
  });
  const [createdJob, setCreatedJob] = useState(null);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePreview = (e) => {
    e.preventDefault();
    if (!formData.client_name || !formData.client_phone || !formData.quote_amount) {
      toast.error('Fill in all required fields');
      return;
    }
    setStep(2);
  };

  const handleSend = async () => {
    setLoading(true);
    
    try {
      // Create the job first
      const jobData = {
        client_name: formData.client_name,
        client_phone: formData.client_phone,
        description: formData.description || `Quote for ${formData.client_name}`,
        quote_amount: parseFloat(formData.quote_amount)
      };
      
      const response = await axios.post(`${API}/jobs`, jobData);
      setCreatedJob(response.data);
      
      // Generate SMS message with customer portal link
      const customerPortalUrl = `${window.location.origin}/portal/${response.data.customer_token}`;
      const message = `Hi ${formData.client_name}! Here's your quote from ${businessName}:\n\n${formData.description || 'Services'}\nTotal: $${formData.quote_amount}\n\nView full details: ${customerPortalUrl}\n\nReply YES to confirm or call us to discuss!`;
      
      // Open SMS with pre-filled message
      window.location.href = `sms:${formData.client_phone}?body=${encodeURIComponent(message)}`;
      
      setStep(3);
      toast.success('Quote created! SMS opened.');
    } catch (error) {
      toast.error('Failed to create quote');
    } finally {
      setLoading(false);
    }
  };

  const businessName = user?.business_name || user?.name;

  return (
    <div className="p-4 animate-fade-in" data-testid="quick-quote-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          data-testid="back-button"
          onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
          className="w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-dark flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#FF5F00]" />
            Quick Quote
          </h1>
          <p className="text-slate-500 text-sm">Send a quote in 30 seconds</p>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
              step >= s ? 'bg-[#FF5F00] text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {s}
            </div>
            {s < 3 && <div className={`w-8 h-1 ${step > s ? 'bg-[#FF5F00]' : 'bg-slate-200'}`} />}
          </div>
        ))}
        <span className="ml-2 text-slate-500 text-sm">
          {step === 1 ? 'Details' : step === 2 ? 'Preview' : 'Done!'}
        </span>
      </div>

      {step === 1 && (
        <form onSubmit={handlePreview} className="space-y-4">
          {/* Client Name */}
          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
              Client Name *
            </Label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                name="client_name"
                data-testid="quick-client-name"
                type="text"
                value={formData.client_name}
                onChange={handleChange}
                placeholder="John Doe"
                className="h-14 pl-12 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 text-lg rounded-lg"
                required
              />
            </div>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
              Phone Number *
            </Label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                name="client_phone"
                data-testid="quick-client-phone"
                type="tel"
                value={formData.client_phone}
                onChange={handleChange}
                placeholder="555-123-4567"
                className="h-14 pl-12 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 text-lg rounded-lg"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
              Work Description
            </Label>
            <div className="relative">
              <FileText className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
              <Textarea
                name="description"
                data-testid="quick-description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Fix leaky faucet, replace washers"
                className="min-h-[80px] pl-12 pt-3 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-lg resize-none"
              />
            </div>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold">
              Quote Amount *
            </Label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                name="quote_amount"
                data-testid="quick-amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.quote_amount}
                onChange={handleChange}
                placeholder="150.00"
                className="h-14 pl-12 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 text-2xl font-heading font-bold rounded-lg"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="preview-quote"
            className="w-full h-14 bg-[#FF5F00] hover:bg-orange-hover text-white font-bold uppercase tracking-wide text-lg rounded-lg shadow-hard"
          >
            Preview Quote
          </Button>
        </form>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {/* Quote Preview Card */}
          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard overflow-hidden">
            <div className="bg-slate-dark p-4">
              <h2 className="font-heading text-xl font-bold text-white">{businessName}</h2>
              <p className="text-slate-400 text-sm">Quote</p>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <p className="text-slate-500 text-sm">To:</p>
                <p className="font-bold text-slate-900">{formData.client_name}</p>
                <p className="text-slate-600">{formData.client_phone}</p>
              </div>
              
              {formData.description && (
                <div>
                  <p className="text-slate-500 text-sm">Work:</p>
                  <p className="text-slate-900">{formData.description}</p>
                </div>
              )}
              
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-slate-700">Total</span>
                  <span className="font-heading text-3xl font-bold text-[#FF5F00]">
                    ${parseFloat(formData.quote_amount).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* SMS Preview */}
          <div className="bg-slate-100 rounded-xl p-4">
            <p className="text-slate-500 text-xs uppercase font-bold mb-2 flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> SMS Preview
            </p>
            <p className="text-slate-700 text-sm whitespace-pre-line">
              Hi {formData.client_name}! Here's your quote from {businessName}:
              {'\n\n'}{formData.description || 'Services'}
              {'\n'}Total: ${formData.quote_amount}
              {'\n\n'}Reply YES to confirm or call us to discuss!
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              data-testid="back-to-edit"
              variant="outline"
              onClick={() => setStep(1)}
              className="flex-1 h-14 text-lg font-bold border-2"
            >
              Edit
            </Button>
            <Button
              data-testid="send-quote"
              onClick={handleSend}
              disabled={loading}
              className="flex-1 h-14 bg-[#FF5F00] hover:bg-orange-hover text-white font-bold uppercase text-lg rounded-lg"
            >
              <Send className="w-5 h-5 mr-2" />
              {loading ? 'Sending...' : 'Send Quote'}
            </Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Zap className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-dark mb-2">
            Quote Sent!
          </h2>
          <p className="text-slate-500 mb-6">
            Job saved. SMS app opened with your quote.
          </p>
          
          <div className="space-y-3">
            <Button
              data-testid="view-job"
              onClick={() => navigate(`/job/${createdJob?.id}`)}
              className="w-full h-14 bg-[#FF5F00] hover:bg-orange-hover text-white font-bold text-lg"
            >
              View Job Details
            </Button>
            <Button
              data-testid="new-quote"
              variant="outline"
              onClick={() => {
                setStep(1);
                setFormData({ client_name: '', client_phone: '', description: '', quote_amount: '' });
                setCreatedJob(null);
              }}
              className="w-full h-14 text-lg font-bold border-2"
            >
              Send Another Quote
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
